import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import Avatar from "@/components/Avatar";

const STREET = "https://images.unsplash.com/photo-1573662766191-066ba9570a4b?crop=entropy&cs=srgb&fm=jpg&q=70&w=900";

// A lightweight "walking down the street" scene that REACTS to the character's state:
// - low energy  -> tired, slow limp
// - high clout  -> flexing swagger with sparkles
export default function WalkingScene({ character }) {
  if (!character) return null;
  const energy = character.needs?.energy ?? 100;
  const clout = character.clout ?? 0;
  const tired = energy < 25;
  const flexing = !tired && clout >= 50;

  const dur = tired ? 1.15 : flexing ? 0.55 : 0.68;
  const bob = tired ? -3 : flexing ? -9 : -7;
  const tilt = tired ? 4 : flexing ? 3 : 2.5; // tired droops forward
  const speed = tired ? "22s" : flexing ? "9s" : "14s";

  let caption, accent;
  if (tired) { caption = `${character.name} dey waka tire, energy don finish 😮‍💨`; accent = "#FF0055"; }
  else if (flexing) { caption = `${character.name} dey waka with swagger ✨`; accent = "#FFD700"; }
  else { caption = `${character.name} dey waka for ${character.district_name} 🚶🏾`; accent = "#16A34A"; }

  return (
    <div className="relative h-36 rounded-2xl overflow-hidden border border-slate-200" data-testid="walking-scene">
      <div className="absolute inset-0 street-scroll" style={{ backgroundImage: `url(${STREET})`, animationDuration: speed }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
      <div className="absolute bottom-7 left-0 right-0 h-[2px]" style={{ background: `${accent}b3`, boxShadow: `0 0 12px ${accent}` }} />

      <div className="absolute bottom-3 left-6 flex flex-col items-center">
        {/* flex sparkles */}
        {flexing && (
          <>
            <Sparkles size={14} className="absolute -top-1 -left-2 text-[#FFD700] flex-sparkle" style={{ animationDelay: "0s" }} />
            <Sparkles size={11} className="absolute -top-2 right-0 text-[#FFD700] flex-sparkle" style={{ animationDelay: "0.9s" }} />
          </>
        )}
        <motion.div
          animate={tired ? { y: [0, -3, 0], rotate: [tilt, tilt + 1, tilt] } : { y: [0, bob, 0], rotate: [-tilt, tilt, -tilt] }}
          transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "bottom center" }}
        >
          <Avatar src={character.avatar} size={64} ring={accent} />
        </motion.div>
        <motion.div
          animate={{ scaleX: [1, 0.7, 1], opacity: [0.55, 0.3, 0.55] }}
          transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
          className="w-12 h-2 rounded-full bg-black blur-[3px] mt-1"
        />
      </div>

      <div className="absolute top-3 right-4 text-right">
        <div className="label-xs neon-text" style={{ color: accent }}>
          {tired ? "Low battery" : flexing ? "Big baller" : "On the move"}
        </div>
        <div className="text-sm text-slate-900 mt-0.5 max-w-[11rem]">{caption}</div>
      </div>
    </div>
  );
}
